/**
 * attention.ts — блок «Требует внимания» (WEB-3781): шапка блока, ошибка
 * загрузки, «Исправит владелец», «Показать ещё», строка «часть проверок не
 * ответила» и заголовки и подписи видов, которых нет в словарях плашек.
 *
 * Что уже было у плашек каркаса — берётся оттуда, а не дублируется здесь:
 * причина заморозки, неоплата, долг и фраза выхода из долга, «Пополнить»,
 * «Управление подпиской», «Перейти в раздел «Баланс»» (`billing.ts`),
 * «Открыть настройки» (`settings.ts`). Заголовки плашек аналитики и старого
 * адреса там же называют проект внутри фразы, а блок проекта имени не пишет
 * (имя ставит только блок организации, в начале заголовка), — поэтому здесь
 * свои заголовки без имени.
 *
 * Черновик текстов — исполнитель (решение владельца 24.10 п. 7: «черновик
 * ru/en пишет трек, владелец правит»). Эталон — `design/cabinet-rescin/
 * sections/home-x.jsx` (`ATT`, `ATT_X`, `FE.att`) и `org-dash-x.jsx` (`attSub`).
 * «Всё в порядке» и подсказку числа у «Главной» в меню завёл WEB-3782,
 * подпись «Всё в порядке» блока организации — WEB-4014, пункт «домен скоро
 * истекает» и кнопку «Открыть домены» — WEB-4014 юнит 2.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

export const attentionStrings = defineDictionary(
  {
    blockTitle: 'Needs attention',
    orgSub: (n: number) =>
      `${n} ${plural('en', n, { one: 'issue', other: 'issues' })} in projects and balance`,
    projectSub: (n: number) =>
      `${n} ${plural('en', n, { one: 'issue', other: 'issues' })} — the sooner, the smaller the losses`,
    loadFailed: 'Could not load the issue list',
    ownerFix: 'The owner can fix this',
    showMore: (n: number) => `Show ${n} more`,
    guide: 'How to',
    unchecked: 'Some checks did not respond — the list may be incomplete',

    frozenTitle: 'The project is suspended',
    pastDueTitle: 'The subscription is unpaid',
    debtTitleNoAmount: 'Credit debt',
    analyticsBrokenTitle: 'Analytics receives no events',
    analyticsBrokenSince: (since: string) =>
      `no events since ${since} — check the connection in project settings`,
    analyticsBrokenNoTime: 'check the connection in project settings',
    analyticsPartialTitle: 'Analytics receives only some events',
    analyticsPartialCounts: (sent: string, ingested: string) =>
      `${sent} sent and ${ingested} accepted in 24 hours — some events are dropped`,
    analyticsPartialNoCounts:
      'some events are dropped — check the event plan in Amplitude',
    analyticsPausedTitle: 'We paused analytics delivery',
    analyticsPausedText:
      'nothing to fix on your side — events will be sent when we resume delivery',
    legacyDomainTitle: (domain: string) =>
      `Domain ${domain} still points to the old address`,
    legacyDomainTitleNoName: 'A domain still points to the old address',
    legacyDomainText:
      'change its CNAME record at the registrar — the new address is in the guide',
    legacyApiTitle: (host: string) =>
      `The app calls the old API address ${host}`,
    legacyApiTitleNoHost: 'The app calls the old API address',
    legacyApiText: (url: string) =>
      `change the API address to ${url} in the app and on your server`,
    legacyApiTextNoUrl:
      'change the API address in the app and on your server — the new one is in the guide',

    // WEB-4014 юнит 2: купленный домен организации скоро закончит регистрацию
    // и сам не продлится (черновик трека, владелец правит).
    domainExpiringTitle: (domain: string, date: string) =>
      `Domain ${domain}: registered until ${date}`,
    domainExpiringTitleNoDate: (domain: string) =>
      `Domain ${domain}: registration ends soon`,
    domainExpiringTitleNoName: 'A domain registration ends soon',
    domainExpiringAutoOff: 'auto-renewal is off — renew the domain in Domains',
    domainExpiringAutoOn:
      'the balance may not cover auto-renewal — top up or renew manually',
    domainExpiringAutoOnNotOwner:
      'the wallet may not have enough for auto-renewal — the owner will fix it',
    domainExpiringRenew: 'renew the domain so its sites keep opening',
    domainRenewalCost: (amount: string) => `renewal: $${amount}`,
    openDomains: 'Open domains',

    // WEB-3782: рядовому участнику на главной проекта при пустом списке
    // (эталон `ATT_X` home-x.jsx) и подсказка числа у «Главной» в меню
    // (эталон `T.att` shell-x.jsx).
    okTitle: 'All good',
    okSub: 'no issues in the project',
    // WEB-4014: блок организации при пустом ответе (решение владельца 28.10).
    orgOkSub: 'no issues in projects and balance',
    navCount: (n: number) =>
      `${n} ${plural('en', n, { one: 'needs', other: 'need' })} attention`,
  },
  {
    blockTitle: 'Требует внимания',
    orgSub: (n: number) =>
      `${n} ${plural('ru', n, { one: 'проблема', few: 'проблемы', many: 'проблем' })} в проектах и балансе`,
    projectSub: (n: number) =>
      `${n} ${plural('ru', n, { one: 'проблема', few: 'проблемы', many: 'проблем' })} — чем раньше, тем меньше потерь`,
    loadFailed: 'Не удалось загрузить список проблем',
    ownerFix: 'Исправит владелец',
    showMore: (n: number) => `Показать ещё ${n}`,
    guide: 'Инструкция',
    unchecked: 'Часть проверок не ответила — список может быть неполным',

    frozenTitle: 'Проект приостановлен',
    pastDueTitle: 'Подписка не оплачена',
    debtTitleNoAmount: 'Долг по кредитам',
    analyticsBrokenTitle: 'Аналитика не получает события',
    analyticsBrokenSince: (since: string) =>
      `событий нет с ${since} — проверьте подключение в настройках проекта`,
    analyticsBrokenNoTime: 'проверьте подключение в настройках проекта',
    analyticsPartialTitle: 'Аналитика получает не все события',
    analyticsPartialCounts: (sent: string, ingested: string) =>
      `за сутки отправлено ${sent}, принято ${ingested} — часть событий отбрасывается`,
    analyticsPartialNoCounts:
      'часть событий отбрасывается — проверьте план событий в Amplitude',
    analyticsPausedTitle: 'Доставку в аналитику приостановили мы',
    analyticsPausedText:
      'чинить ничего не нужно — события уйдут, когда мы возобновим доставку',
    legacyDomainTitle: (domain: string) =>
      `Домен ${domain} ещё смотрит на старый адрес`,
    legacyDomainTitleNoName: 'Домен ещё смотрит на старый адрес',
    legacyDomainText:
      'поменяйте его CNAME-запись у регистратора — новый адрес в инструкции',
    legacyApiTitle: (host: string) =>
      `Приложение обращается к старому адресу API ${host}`,
    legacyApiTitleNoHost: 'Приложение обращается к старому адресу API',
    legacyApiText: (url: string) =>
      `поменяйте адрес API на ${url} в приложении и на своём сервере`,
    legacyApiTextNoUrl:
      'поменяйте адрес API в приложении и на своём сервере — новый адрес в инструкции',

    domainExpiringTitle: (domain: string, date: string) =>
      `Домен ${domain}: регистрация до ${date}`,
    domainExpiringTitleNoDate: (domain: string) =>
      `Домен ${domain}: скоро конец регистрации`,
    domainExpiringTitleNoName: 'Скоро конец регистрации домена',
    domainExpiringAutoOff:
      'автопродление выключено — продлите домен в разделе «Домены»',
    domainExpiringAutoOn:
      'на балансе может не хватить на автопродление — пополните баланс или продлите вручную',
    domainExpiringAutoOnNotOwner:
      'может не хватить денег на кошельке — исправит владелец',
    domainExpiringRenew:
      'продлите домен, чтобы сайты на нём не перестали открываться',
    domainRenewalCost: (amount: string) => `продление — $${amount}`,
    openDomains: 'Открыть домены',

    okTitle: 'Всё в порядке',
    okSub: 'в проекте нет проблем',
    orgOkSub: 'в проектах и балансе нет проблем',
    navCount: (n: number) =>
      `${n} ${plural('ru', n, { one: 'требует', few: 'требуют', many: 'требуют' })} внимания`,
  },
);
